import React, { useState, useEffect, useRef, useCallback } from 'react';
import { garantie as garApi } from '../services/api';
import { DocumentViewer, PdfThumbnail } from './shared/DocumentViewer';
import './Garantie.css';

import { texteTheme } from '../utils/theme';
// ── Helpers ──────────────────────────────────────────────────────
function formatDate(d) {
  if (!d) return '—';
  const [y, m, day] = (typeof d === 'string' ? d : '').split('T')[0].split('-').map(Number);
  return (y && m && day) ? new Date(y, m - 1, day).toLocaleDateString('fr-FR') : '—';
}

function formatMontant(m) {
  if (m == null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(m);
}

function formatRelative(d) {
  if (!d) return '';
  const date = new Date(d);
  const diff = Date.now() - date.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  if (j < 7) return `il y a ${j} j`;
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getInitials(label) {
  if (!label) return '?';
  const parts = label.trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p[0]?.toUpperCase() || '').join('') || '?';
}

// Sémantique des statuts constructeur (par nom, cf. table statuts_garantie)
const APPROUVE_STATUTS    = new Set(['Approuvé', 'Approuvé partiellement']);
const REFUSE_STATUT       = 'Refusé';
const RETRAITEMENT_STATUT = 'Retraitement nécessaire';

const STATUTS_ROULANTE = [
  { value: 'a_soumettre', label: 'À soumettre', color: 'var(--color-warning)', bgLight: 'var(--color-warning-light)' },
  { value: 'soumis',      label: 'Soumis',      color: 'var(--color-info)',    bgLight: 'var(--color-info-light)'    },
  { value: 'non_soumis',  label: 'Non soumis',  color: 'var(--color-error)',   bgLight: 'var(--color-error-light)'   },
];

// Auteur affiché pour les commentaires importés legacy (source='sigar')
const CONSTRUCTEUR_LABEL = 'Constructeur';

// ── Vignette document (réutilise les classes .doc-thumbnail / .doc-preview / .doc-icon
//    déjà tokenisées dans Litiges.css ; on n'ajoute que la sémantique propre Garantie) ──
function DocThumbnail({ doc, canDelete, onRemove, onOpen }) {
  const mimeType = doc.type || doc.type_mime || '';
  const isImage  = mimeType.startsWith('image/');
  const isPdf    = mimeType === 'application/pdf';
  const name     = doc.name || doc.nom || 'document';

  return (
    <div className={`doc-thumbnail${doc.uploading ? ' uploading' : ''}`} title={name}>
      {doc.uploading && (
        <div className="doc-uploading-overlay">
          <span className="doc-uploading-spinner" />
        </div>
      )}
      {canDelete && !doc.uploading && (
        <button
          className="doc-remove-btn"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          title="Supprimer ce document"
        >✕</button>
      )}
      <div className="doc-preview" onClick={!doc.uploading ? onOpen : undefined}>
        {isImage && doc.url ? (
          <img src={doc.url} alt={name} />
        ) : isPdf && doc.url ? (
          <PdfThumbnail url={doc.url} />
        ) : (
          <div className="doc-icon">
            <span className="doc-icon-emoji">📎</span>
            <span className="doc-icon-label">DOC</span>
          </div>
        )}
      </div>
      <div className="doc-name">{name}</div>
    </div>
  );
}

// ── Modale principale ─────────────────────────────────────────────
export default function GarantieModal({ garantieId, user, statuts = [], motifs = [], onClose, onStatutChange }) {
  const canDelete = user.role === 'super-admin' || user.role === 'admin';

  // — Détail + activités —
  const [detail,        setDetail]        = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [commentaires,  setCommentaires]  = useState([]);
  const [documents,     setDocuments]     = useState([]);

  // — Édition (zone État du dossier) —
  const [editStatutRoulante,   setEditStatutRoulante]   = useState('a_soumettre');
  const [editStatut,           setEditStatut]           = useState('');
  const [editMotif,            setEditMotif]            = useState('');
  const [editDateRevision,     setEditDateRevision]     = useState('');
  const [editMontantRembourse, setEditMontantRembourse] = useState('');
  const [editMontantDemande,   setEditMontantDemande]   = useState('');
  const [editRetraitementMsg,  setEditRetraitementMsg]  = useState(''); // textarea inline si Retraitement

  // — Activité —
  const [saving,        setSaving]        = useState(false);
  const [newComment,    setNewComment]    = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [lightbox,      setLightbox]      = useState(null);
  const [docToDelete,   setDocToDelete]   = useState(null);
  const [dragging,      setDragging]      = useState(false);
  const fileInputRef = useRef(null);

  // — Historique —
  const [logs,         setLogs]         = useState([]);
  const [showLogs,     setShowLogs]     = useState(false);
  const [loadingLogs,  setLoadingLogs]  = useState(false);

  const ACTION_LABELS = {
    statut_roulante:         'État du dossier',
    statut:                  'Statut constructeur',
    motif:                   'Motif',
    date_revision:           'Prochaine action',
    action_texte:            'Action',
    commentaire_ajout:       'Commentaire ajouté',
    commentaire_suppression: 'Commentaire supprimé',
    document_ajout:          'Document ajouté',
    document_suppression:    'Document supprimé',
    montant_rembourse:       'Montant remboursé',
    montant_demande:         'Montant demandé',
    montant_approuve:        'Montant approuvé',
  };

  // — Chargement initial —
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [detailRes, comRes, docRes] = await Promise.all([
          garApi.getById(garantieId),
          garApi.getCommentaires(garantieId),
          garApi.getDocuments(garantieId),
        ]);
        const d = detailRes.data;
        setDetail(d);
        setEditStatutRoulante(d.statut_roulante || 'a_soumettre');
        setEditStatut(d.statut || '');
        setEditMotif(d.motif || '');
        setEditDateRevision(d.date_revision ? d.date_revision.split('T')[0] : '');
        setEditMontantRembourse(d.montant_rembourse != null ? String(d.montant_rembourse) : '');
        setEditMontantDemande(d.montant_demande != null ? String(d.montant_demande) : '');
        setCommentaires(comRes.data.commentaires || []);
        setDocuments(docRes.data.documents || []);
      } catch (err) {
        console.error('[GarantieModal] Erreur chargement:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [garantieId]);

  // — Machine d'état : quels champs s'affichent ? —
  const isSoumis        = editStatutRoulante === 'soumis';
  const isNonSoumis     = editStatutRoulante === 'non_soumis';
  const showsRembourse  = isSoumis && APPROUVE_STATUTS.has(editStatut);
  const showsMotifRefus = (isSoumis && editStatut === REFUSE_STATUT) || isNonSoumis;
  const showsRetraite   = isSoumis && editStatut === RETRAITEMENT_STATUT;

  // — Save —
  const handleSave = async () => {
    setSaving(true);
    try {
      // Accepte la saisie FR : virgule décimale + espaces (séparateurs de milliers,
      // y compris l'espace insécable). Champs en type="text" pour préserver la virgule.
      const parseMontant = (v) => {
        if (v == null || v.toString().trim() === '') return null;
        const n = parseFloat(v.toString().replace(/\s/g, '').replace(',', '.'));
        return Number.isFinite(n) ? n : null;
      };
      const remParsed = parseMontant(editMontantRembourse);
      const demParsed = parseMontant(editMontantDemande);

      const payload = {
        statut_roulante: editStatutRoulante,
        statut: isSoumis ? (editStatut || null) : null,
        // motif n'est sauvegardé que si pertinent (refus ou non soumis)
        motif: showsMotifRefus ? (editMotif || null) : null,
        date_revision: editDateRevision || null,
      };

      if (isSoumis) {
        payload.montant_demande = Number.isFinite(demParsed) ? demParsed : null;
      }
      // Montant remboursé : envoyé seulement quand on est dans un état où il s'affiche.
      // Si on quitte cet état, on n'écrase pas la valeur en BDD (la prochaine sync legacy peut continuer).
      if (showsRembourse) {
        payload.montant_rembourse = Number.isFinite(remParsed) ? remParsed : null;
      }

      await garApi.updateStatut(garantieId, payload);

      // Push commentaire pour Retraitement si saisi (avant le reload de la liste)
      const retraiteMsg = editRetraitementMsg.trim();
      if (showsRetraite && retraiteMsg) {
        try {
          await garApi.addCommentaire(garantieId, retraiteMsg);
          const r = await garApi.getCommentaires(garantieId);
          setCommentaires(r.data.commentaires || []);
          setEditRetraitementMsg('');
        } catch (e) {
          console.warn('[GarantieModal] commentaire retraitement échec:', e);
        }
      }

      setDetail(prev => ({
        ...prev,
        statut_roulante: editStatutRoulante,
        statut: editStatut,
        motif: showsMotifRefus ? editMotif : prev?.motif,
        date_revision: editDateRevision || null,
        ...(isSoumis ? {
          montant_demande: Number.isFinite(demParsed) ? demParsed : null,
          montant_demande_manuel: Number.isFinite(demParsed),
        } : {}),
        ...(showsRembourse ? {
          montant_rembourse: Number.isFinite(remParsed) ? remParsed : null,
          montant_rembourse_manuel: Number.isFinite(remParsed),
        } : {}),
      }));
      // Remonte un patch complet au tableau parent pour MAJ instantanée (statut,
      // motif, date + montants saisis) — sans attendre un rechargement.
      const patch = {
        statut_roulante: editStatutRoulante,
        statut: isSoumis ? (editStatut || null) : null,
        motif: showsMotifRefus ? (editMotif || null) : null,
        date_revision: editDateRevision || null,
        ...(isSoumis ? { montant_demande: Number.isFinite(demParsed) ? demParsed : null, montant_demande_manuel: Number.isFinite(demParsed) } : {}),
        ...(showsRembourse ? { montant_rembourse: Number.isFinite(remParsed) ? remParsed : null, montant_rembourse_manuel: Number.isFinite(remParsed) } : {}),
      };
      onStatutChange && onStatutChange(garantieId, patch);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // — Commentaires —
  const handleAddComment = async () => {
    if (!newComment.trim() || savingComment) return;
    setSavingComment(true);
    try {
      await garApi.addCommentaire(garantieId, newComment.trim());
      setNewComment('');
      const r = await garApi.getCommentaires(garantieId);
      setCommentaires(r.data.commentaires || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setSavingComment(false);
    }
  };

  const handleDeleteComment = async (cid) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;
    try {
      await garApi.deleteCommentaire(garantieId, cid);
      setCommentaires(prev => prev.filter(c => c.id !== cid));
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  // — Documents —
  const uploadFile = useCallback(async (file) => {
    const tempId = Date.now() + Math.random();
    const tempDoc = {
      id: tempId,
      name: file.name,
      type: file.type,
      url: URL.createObjectURL(file),
      uploading: true,
    };
    setDocuments(prev => [...prev, tempDoc]);
    try {
      const presignRes = await garApi.presignUpload(garantieId, { filename: file.name, contentType: file.type });
      const { uploadUrl, key } = presignRes.data;
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      const confirmRes = await garApi.confirmUpload(garantieId, {
        key, filename: file.name, contentType: file.type, size: file.size,
      });
      URL.revokeObjectURL(tempDoc.url);
      setDocuments(prev => prev.map(d => d.id === tempId ? confirmRes.data.document : d));
    } catch (err) {
      console.error('[GarantieModal] Upload échec:', err);
      URL.revokeObjectURL(tempDoc.url);
      setDocuments(prev => prev.filter(d => d.id !== tempId));
      alert(`Erreur lors de l'upload de « ${file.name} »`);
    }
  }, [garantieId]);

  const addFiles = useCallback((files) => {
    Array.from(files).forEach(uploadFile);
  }, [uploadFile]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const handleDeleteDoc = async (doc) => {
    try {
      await garApi.deleteDocument(garantieId, doc.id);
      if (doc.url?.startsWith('blob:')) URL.revokeObjectURL(doc.url);
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression du document');
    }
  };

  // — Historique —
  const handleToggleLogs = async () => {
    if (!showLogs && logs.length === 0) {
      setLoadingLogs(true);
      try {
        const r = await garApi.getLogs(garantieId);
        setLogs(r.data.logs || []);
      } catch (err) {
        console.error('[GarantieModal] Erreur logs:', err);
      } finally {
        setLoadingLogs(false);
      }
    }
    setShowLogs(v => !v);
  };

  // — Bouton sucre : marquer soumis depuis l'état initial —
  const markAsSoumis = () => {
    setEditStatutRoulante('soumis');
    if (!editStatut) setEditStatut('En attente retour constructeur');
  };

  // — Style d'une pill statut interne actif —
  const pillStyle = (s, active) => active
    ? { background: s.color, borderColor: s.color, color: 'var(--bg-surface)' }
    : { borderColor: s.color, color: texteTheme(s.color) };

  return (
    <div className="gar-modal-overlay">
      <div className="gar-modal" role="dialog" aria-modal="true" aria-labelledby="gar-modal-title" onClick={e => e.stopPropagation()}>
        {/* ── Header sticky ── */}
        <div className="gar-modal-header">
          <div className="gar-modal-head-id">
            <div className="gar-modal-head-line1">
              <span id="gar-modal-title" className="gar-modal-numfact">
                {detail ? detail.num_facture : `Dossier #${garantieId}`}
              </span>
              {detail?.marque && <span className="gar-modal-marque">{detail.marque}</span>}
              {detail?.chassis && <span className="gar-modal-chassis">{detail.chassis}</span>}
            </div>
            {detail && (
              <div className="gar-modal-head-line2">
                <span>{detail.concession_nom || detail.stdos || '—'}</span>
                <span className="sep">·</span>
                <span>OR {detail.num_or || '—'}</span>
                <span className="sep">·</span>
                <span>Garantie {formatDate(detail.date_garantie)}</span>
              </div>
            )}
          </div>
          {detail && (
            <div className="gar-modal-head-facture">
              <span className="gar-modal-head-facture-label">Facturé</span>
              <span className="gar-modal-head-facture-val">{formatMontant(detail.montant)}</span>
            </div>
          )}
          <button className="gar-modal-close" onClick={onClose} aria-label="Fermer">✕</button>
        </div>

        {loading ? (
          <div className="gar-modal-loading">Chargement…</div>
        ) : detail ? (
          <div className="gar-modal-body">
            {/* ────────── Zone : État du dossier ────────── */}
            <section className="gar-state-card" aria-label="État du dossier">
              <h3 className="gar-state-title">État du dossier</h3>

              <div className="gar-state-pills" role="radiogroup" aria-label="Statut interne">
                {STATUTS_ROULANTE.map(s => (
                  <button
                    key={s.value}
                    type="button"
                    role="radio"
                    aria-checked={editStatutRoulante === s.value}
                    className={`gar-pill ${editStatutRoulante === s.value ? 'active' : ''}`}
                    style={pillStyle(s, editStatutRoulante === s.value)}
                    onClick={() => {
                      setEditStatutRoulante(s.value);
                      if (s.value !== 'soumis') setEditStatut('');
                      else if (!editStatut) setEditStatut('En attente retour constructeur');
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* À soumettre : hint + raccourci */}
              {editStatutRoulante === 'a_soumettre' && (
                <div className="gar-fade-in">
                  <p className="gar-state-hint">
                    Soumettez le dossier au constructeur pour activer les champs suivants.
                  </p>
                  <button type="button" className="gar-state-cta" onClick={markAsSoumis}>
                    Marquer comme soumis
                  </button>
                </div>
              )}

              {/* Soumis : statut constructeur + montant demandé (+ champs dérivés) */}
              {isSoumis && (
                <div className="gar-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label
                      className="gar-state-title"
                      style={{ display: 'block', marginBottom: 8 }}
                    >Statut chez le constructeur</label>
                    <div className="gar-state-pills" role="radiogroup" aria-label="Statut constructeur">
                      {statuts.map(s => {
                        const active = editStatut === s.value;
                        return (
                          <button
                            key={s.value}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            className={`gar-pill gar-pill-constructeur ${active ? 'active' : ''}`}
                            style={active
                              ? { background: s.color, borderColor: s.color, color: 'var(--bg-surface)' }
                              : { borderColor: s.color, color: texteTheme(s.color) }}
                            onClick={() => setEditStatut(s.value)}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="gar-state-fields">
                    <div className="gar-state-field">
                      <label htmlFor="gar-montant-demande">Montant demandé au constructeur</label>
                      <input
                        id="gar-montant-demande"
                        type="text"
                        inputMode="decimal"
                        className="gar-input"
                        placeholder="0,00"
                        value={editMontantDemande}
                        onChange={e => setEditMontantDemande(e.target.value)}
                      />
                    </div>

                    {showsRembourse && (
                      <div className="gar-state-field gar-fade-in">
                        <label htmlFor="gar-montant-rembourse">Montant remboursé</label>
                        <input
                          id="gar-montant-rembourse"
                          type="text"
                          inputMode="decimal"
                          className="gar-input"
                          placeholder="0,00"
                          value={editMontantRembourse}
                          onChange={e => setEditMontantRembourse(e.target.value)}
                        />
                      </div>
                    )}

                    {/* Relevé constructeur — LECTURE SEULE. Il ne s'agit pas d'une
                        saisie mais de ce que le constructeur a réellement payé,
                        avec le fichier d'où sort le chiffre : sans cette
                        provenance, le montant n'est plus justifiable plus tard. */}
                    {detail.releve_part != null && (
                      <div className="gar-state-field gar-state-field-full gar-fade-in">
                        <label>Relevé constructeur</label>
                        <div className="gar-releve-dossier">
                          <div className="gar-releve-dossier-montant">
                            <span>{formatMontant(detail.releve_part)}</span>
                            <span className="gar-releve-lock" title="Issu des relevés importés — non modifiable ici">🔒</span>
                          </div>
                          {detail.releve_nb_dossiers > 1 && (
                            <div className="gar-releve-dossier-partage">
                              Part de ce dossier : l'OR totalise {formatMontant(detail.releve_or)} au relevé,
                              répartis entre {detail.releve_nb_dossiers} dossiers au prorata du facturé.
                            </div>
                          )}
                          <ul className="gar-releve-dossier-sources">
                            {(detail.releves || []).map(r => (
                              <li key={r.id}>
                                <span className="gar-releve-dossier-nom">{r.releve_nom}</span>
                                {r.mois && r.annee ? <span className="sep"> · </span> : null}
                                {r.mois && r.annee ? `${String(r.mois).padStart(2, '0')}/${r.annee}` : ''}
                                {r.claim_no ? <><span className="sep"> · </span>{r.claim_no}</> : null}
                                <span className="sep"> · </span>
                                <strong>{formatMontant(r.montant)}</strong>
                                {r.cloture_le && <span className="gar-releve-tag cloture">✅ clôturé</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    <div className="gar-state-field">
                      <label htmlFor="gar-date-revision">Date de prochaine action</label>
                      <input
                        id="gar-date-revision"
                        type="date"
                        className="gar-input"
                        value={editDateRevision}
                        onChange={e => setEditDateRevision(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Motif de refus ou non soumission */}
              {showsMotifRefus && (
                <div className="gar-state-field gar-state-field-full gar-fade-in">
                  <label htmlFor="gar-motif">Motif de refus ou non soumission</label>
                  <select
                    id="gar-motif"
                    className="gar-select"
                    value={editMotif}
                    onChange={e => setEditMotif(e.target.value)}
                  >
                    <option value="">Aucun motif</option>
                    {motifs.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Commentaire à ajouter (Retraitement nécessaire) */}
              {showsRetraite && (
                <div className="gar-state-field gar-state-field-full gar-fade-in">
                  <label htmlFor="gar-retraite-msg">Commentaire à ajouter à la conversation</label>
                  <textarea
                    id="gar-retraite-msg"
                    className="gar-textarea"
                    rows={3}
                    placeholder="Précisez ce qui doit être retraité…"
                    value={editRetraitementMsg}
                    onChange={e => setEditRetraitementMsg(e.target.value)}
                  />
                </div>
              )}

              {/* Date prochaine action quand Non soumis (en plus du motif) */}
              {isNonSoumis && (
                <div className="gar-state-field gar-fade-in" style={{ maxWidth: 260 }}>
                  <label htmlFor="gar-date-revision-ns">Date de prochaine action</label>
                  <input
                    id="gar-date-revision-ns"
                    type="date"
                    className="gar-input"
                    value={editDateRevision}
                    onChange={e => setEditDateRevision(e.target.value)}
                  />
                </div>
              )}

              <div className="gar-state-footer">
                <span className="gar-state-meta">
                  {detail.action_user_nom
                    ? `Dernière action par ${detail.action_user_prenom} ${detail.action_user_nom} ${formatRelative(detail.action_updated_at)}`
                    : 'Aucune action enregistrée'}
                </span>
                <button
                  type="button"
                  className="gar-btn-save"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </section>

            {/* ────────── Zone : Documents + Conversation ────────── */}
            <div className="gar-activity">
              {/* Documents */}
              <section className="gar-card" aria-label="Documents">
                <h3 className="gar-card-title">
                  Documents <span className="gar-card-count">({documents.length})</span>
                </h3>
                <div className="gar-docs-grid">
                  {documents.map(doc => (
                    <DocThumbnail
                      key={doc.id}
                      doc={doc}
                      canDelete={canDelete}
                      onRemove={() => setDocToDelete(doc)}
                      onOpen={() => !doc.uploading && setLightbox(doc)}
                    />
                  ))}
                  <div
                    className={`drop-zone${dragging ? ' dragging' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    title="Glisser-déposer ou cliquer pour ajouter un document"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.msg"
                      style={{ display: 'none' }}
                      onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />
                    <div className="drop-zone-content">
                      <span className="drop-icon">📤</span>
                      <span>Glisser-déposer<br />ou cliquer</span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Conversation */}
              <section className="gar-card" aria-label="Conversation">
                <h3 className="gar-card-title">
                  Conversation <span className="gar-card-count">({commentaires.length})</span>
                </h3>
                <div className="gar-thread">
                  {commentaires.length === 0 && (
                    <div className="gar-thread-empty">Aucun commentaire pour ce dossier.</div>
                  )}
                  {commentaires.map(c => {
                    const isImported = c.source === 'sigar';
                    const authorLabel = isImported ? CONSTRUCTEUR_LABEL : (c.user_label || 'Inconnu');
                    return (
                      <div key={c.id} className="gar-msg">
                        <div className="gar-msg-avatar" aria-hidden="true">
                          {getInitials(authorLabel)}
                        </div>
                        <div className="gar-msg-content">
                          <div className="gar-msg-meta">
                            <span className="gar-msg-author">{authorLabel}</span>
                            <span className="gar-msg-time">{formatRelative(c.created_at)}</span>
                            {canDelete && !isImported && (
                              <button
                                className="gar-msg-del"
                                onClick={() => handleDeleteComment(c.id)}
                                title="Supprimer ce commentaire"
                              >✕</button>
                            )}
                          </div>
                          <div className="gar-msg-body">{c.contenu}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="gar-msg-form">
                  <textarea
                    className="gar-textarea"
                    rows={3}
                    placeholder="Ajouter un commentaire…"
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    onKeyDown={e => {
                      if ((e.key === 'Enter') && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        handleAddComment();
                      }
                    }}
                  />
                  <div className="gar-msg-form-footer">
                    <span className="gar-msg-form-hint">Cmd/Ctrl + Entrée pour envoyer</span>
                    <button
                      type="button"
                      className="gar-btn-send"
                      onClick={handleAddComment}
                      disabled={savingComment || !newComment.trim()}
                    >
                      {savingComment ? 'Envoi…' : 'Envoyer'}
                    </button>
                  </div>
                </div>
              </section>
            </div>

            {/* ────────── Historique ────────── */}
            <section className="gar-history">
              <button
                type="button"
                className={`gar-history-toggle${showLogs ? ' open' : ''}`}
                onClick={handleToggleLogs}
                aria-expanded={showLogs}
              >
                <span>Historique{logs.length > 0 ? ` (${logs.length})` : ''}</span>
                <span className="gar-history-toggle-chevron">▶</span>
              </button>
              {showLogs && (
                <div className="gar-history-list">
                  {loadingLogs && <div className="gar-thread-empty">Chargement…</div>}
                  {!loadingLogs && logs.length === 0 && (
                    <div className="gar-thread-empty">Aucun historique pour ce dossier.</div>
                  )}
                  {!loadingLogs && logs.map(l => (
                    <div key={l.id} className="gar-log-item">
                      <span className="gar-log-badge">{ACTION_LABELS[l.action_type] || l.action_type}</span>
                      <span className="gar-log-detail">{l.detail}</span>
                      <span className="gar-log-meta">
                        {l.user_label} · {formatRelative(l.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        ) : null}
      </div>

      {/* Lightbox (réutilise composant partagé) */}
      {lightbox && (
        <DocumentViewer doc={lightbox} onClose={() => setLightbox(null)} />
      )}

      {/* Confirmation suppression doc */}
      {docToDelete && (
        <div className="doc-confirm-overlay" onClick={() => setDocToDelete(null)}>
          <div className="doc-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <p className="doc-confirm-msg">Supprimer le document&nbsp;?</p>
            <p className="doc-confirm-name">« {docToDelete.name || docToDelete.nom} »</p>
            <div className="doc-confirm-actions">
              <button type="button" className="btn-secondary" onClick={() => setDocToDelete(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={() => { handleDeleteDoc(docToDelete); setDocToDelete(null); }}
              >
                🗑️ Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
